import { MerchantStatusCell } from "Components/Hunter/Merchants/MerchantsList/MerchantStatusCell";
import { LICENSE_CODE_FILTER_OPTIONS } from "Components/Licenses/licenseUtils";
import type { LicenseCode, MerchantListItemDto } from "Infrastructure/Api/Api";
import { formatDate } from "Utils/DateUtils";
import { appUrl } from "Utils/UrlUtils";
import { useTableLocalization } from "Utils/TableLocalizationUtils";
import { useTableRowOnClickHandler } from "Hooks/useTableRowOnClickHandler";
import {
  type MRT_ColumnDef,
  type MRT_TableOptions,
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import { type TFunction, Resources, useResource } from "Translations/Resources";
import { useMemo } from "react";

const createColumns = (
  t: TFunction,
): Array<MRT_ColumnDef<MerchantListItemDto>> => [
  {
    accessorKey: "merchantStatus",
    header: t(Resources.Pages.Content.Merchants.TableHeaders.Status),
    Cell: ({ row }) => (
      <MerchantStatusCell status={row.original.merchantStatus} />
    ),
    filterVariant: "multi-select",
    filterSelectOptions: [
      {
        label: t(Resources.Merchant.MerchantStatus.InDraft),
        value: "InDraft",
      },
      {
        label: t(
          Resources.Merchant.MerchantStatus.InvitedWaitingForConfirmation,
        ),
        value: "InvitedWaitingForConfirmation",
      },
      {
        label: t(Resources.Merchant.MerchantStatus.Onboarding),
        value: "Onboarding",
      },
      {
        label: t(Resources.Merchant.MerchantStatus.WaitingForPaymentTerminal),
        value: "WaitingForPaymentTerminal",
      },
      { label: t(Resources.Merchant.MerchantStatus.Active), value: "Active" },
      {
        label: t(Resources.Merchant.MerchantStatus.BlockedFinancialServices),
        value: "BlockedFinancialServices",
      },
    ],
    size: 0,
  },
  {
    accessorKey: "party.companyNumber",
    header: t(Resources.Pages.Content.Merchants.TableHeaders.CompanyNumber),
    accessorFn: ({ party }) => party.companyNumber || "",
    size: 70,
  },
  {
    accessorKey: "party.companyName",
    header: t(Resources.Pages.Content.Merchants.TableHeaders.CompanyName),
    accessorFn: ({ party }) => party.companyName || "",
    size: 200,
  },
  {
    accessorKey: "license.companyName",
    header: t(Resources.Pages.Content.Merchants.TableHeaders.LicenseOwner),
    size: 200,
    accessorFn: ({ license }) => license.companyName || "",
    Cell: ({ cell }) => cell.getValue<string>() || "-",
  },
  {
    accessorKey: "license.code",
    header: t(Resources.Pages.Content.Merchants.TableHeaders.LicenseType),
    size: 130,
    Cell: ({ cell }) => {
      const code = cell.getValue<LicenseCode>();
      return code || "-";
    },
    filterVariant: "multi-select",
    filterSelectOptions: LICENSE_CODE_FILTER_OPTIONS,
  },
  {
    accessorKey: "license.licenseNo",
    header: t(Resources.Pages.Content.Merchants.TableHeaders.LicenseNumber),
    size: 150,
    Cell: ({ cell }) => cell.getValue<string>() || "-",
  },
  {
    accessorKey: "dateCreated",
    header: t(Resources.Pages.Content.Merchants.TableHeaders.DateCreated),
    size: 175,
    filterVariant: "date",
    Cell: ({ cell }) => formatDate(cell.getValue<Date>()),
  },
  {
    accessorKey: "transactionsCount",
    header: t(Resources.Pages.Content.Merchants.TableHeaders.TransactionsCount),
    size: 150,
    enableColumnFilter: false,
  },
];

type Props = {
  data: Array<MerchantListItemDto>;
  isRefetching?: boolean;
  isLoading?: boolean;
  rowCount: number;
  tableProps: Partial<MRT_TableOptions<MerchantListItemDto>>;
};

export const MerchantListTable = ({
  data,
  isRefetching,
  isLoading,
  rowCount,
  tableProps,
}: Props) => {
  const { t } = useResource();
  const localization = useTableLocalization();
  const createRowClickHandler = useTableRowOnClickHandler();
  const columns = useMemo(() => createColumns(t), [t]);

  const table = useMaterialReactTable({
    ...tableProps,
    data,
    columns,
    rowCount,
    enableColumnResizing: true,
    columnResizeMode: "onChange",
    localization,
    state: {
      ...tableProps.state,
      isLoading,
      showProgressBars: isRefetching,
    },
    initialState: {
      ...tableProps.initialState,
      density: "compact",
    },
    muiTableBodyRowProps: ({ row }) => {
      const url = `${appUrl("merchant-detail")}/${row.original.publicID}`;
      const onClick = createRowClickHandler(url);
      return {
        onClick,
        sx: { cursor: "pointer" },
      };
    },
    muiTablePaperProps: {
      sx: { borderRadius: "10px" },
    },
  });

  return <MaterialReactTable table={table} />;
};
